import React, { useEffect, useState } from "react";
import { api } from "../../services/api";

interface LogEntry {
  timestamp: string;
  type: string;
  message: string;
  stack?: string;
}

const ErrorLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await api.get("/backend/logs");
        if (response.data && response.data.logs) {
          // Parse JSONL (newline-separated JSON)
          const rawData = response.data.logs;
          const lines = rawData.trim().split("\n");
          const parsedLogs = lines
            .map((line: string) => {
              try {
                return JSON.parse(line);
              } catch (e) {
                console.error("Failed to parse log line:", line, e);
                return null;
              }
            })
            .filter((log: any) => log !== null && log.type === "ERROR") // Filter for ERROR types
            .reverse(); // Newest first

          setLogs(parsedLogs);
        } else {
          setError("No log data received from server.");
        }
      } catch (err: any) {
        console.error("Error fetching logs:", err);
        setError(err.response?.data?.message || err.message || "Failed to fetch logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Backend Error Logs</h1>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#44304e] text-white rounded hover:bg-[#36263e] transition-colors"
          >
            Refresh Logs
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#44304e]"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 text-red-700">
            <p>{error}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            <p className="text-xl">No error logs found in app.jsonl.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50 hover:bg-gray-100 transition-colors"}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          {log.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words max-w-md">
                        {log.message}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {log.stack ? (
                          <details className="cursor-pointer">
                            <summary className="text-[#44304e] hover:underline">View Stack Trace</summary>
                            <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-xs rounded-md overflow-x-auto font-mono max-h-48 whitespace-pre-wrap">
                              {log.stack}
                            </pre>
                          </details>
                        ) : (
                          <span className="italic text-gray-400">No stack trace</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorLogsPage;
